/** Normalized pivot (0–1) within an object's box. Default = center. */
export type PivotNorm = { ox: number; oy: number };

const NAMED_PIVOTS: Record<string, PivotNorm> = {
  center: { ox: 0.5, oy: 0.5 },
  middle: { ox: 0.5, oy: 0.5 },
  'top-left': { ox: 0, oy: 0 },
  'top-center': { ox: 0.5, oy: 0 },
  top: { ox: 0.5, oy: 0 },
  'top-right': { ox: 1, oy: 0 },
  'middle-left': { ox: 0, oy: 0.5 },
  left: { ox: 0, oy: 0.5 },
  'middle-right': { ox: 1, oy: 0.5 },
  right: { ox: 1, oy: 0.5 },
  'bottom-left': { ox: 0, oy: 1 },
  'bottom-center': { ox: 0.5, oy: 1 },
  bottom: { ox: 0.5, oy: 1 },
  'bottom-right': { ox: 1, oy: 1 },
};

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0.5;
  return Math.min(1, Math.max(0, n));
}

/** Parse pivot_point string: named presets or free "ox,oy" normalized fractions. */
export function parsePivot(pivotPoint?: string | null): PivotNorm {
  if (!pivotPoint?.trim()) return { ox: 0.5, oy: 0.5 };
  const key = pivotPoint.trim().toLowerCase();
  const named = NAMED_PIVOTS[key];
  if (named) return { ...named };

  const parts = key.split(/[,;\s]+/).filter(Boolean);
  if (parts.length >= 2) {
    const ox = clamp01(Number.parseFloat(parts[0]!));
    const oy = clamp01(Number.parseFloat(parts[1]!));
    return { ox, oy };
  }
  return { ox: 0.5, oy: 0.5 };
}

/** Serialize free pivot as compact normalized string (or "center" when dead-center). */
export function formatPivot(ox: number, oy: number): string {
  const nx = clamp01(ox);
  const ny = clamp01(oy);
  if (Math.abs(nx - 0.5) < 1e-4 && Math.abs(ny - 0.5) < 1e-4) return 'center';
  return `${roundPivot(nx)},${roundPivot(ny)}`;
}

function roundPivot(n: number): string {
  const r = Math.round(n * 10000) / 10000;
  return String(r);
}

export function pivotOffsetPx(
  pivot: PivotNorm,
  width: number,
  height: number
): { x: number; y: number } {
  return { x: pivot.ox * width, y: pivot.oy * height };
}

export function pivotCssPercent(pivot: PivotNorm): { x: string; y: string } {
  return {
    x: `${pivot.ox * 100}%`,
    y: `${pivot.oy * 100}%`,
  };
}

function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Rotate (x,y) around (cx,cy) by deg. */
export function rotateAround(
  x: number,
  y: number,
  cx: number,
  cy: number,
  deg: number
): { x: number; y: number } {
  if (!deg) return { x, y };
  const rad = toRadians(deg);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = x - cx;
  const dy = y - cy;
  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
}

/**
 * When changing pivot fractions under a non-zero rotation, adjust top-left so the
 * visual does not jump (keeps transformed appearance).
 */
export function compensateTopLeftForPivotChange(
  x: number,
  y: number,
  width: number,
  height: number,
  rotation: number,
  from: PivotNorm,
  to: PivotNorm
): { x: number; y: number } {
  const dx = (from.ox - to.ox) * width;
  const dy = (from.oy - to.oy) * height;
  if (!rotation) return { x, y };
  const rotated = rotateAround(dx, dy, 0, 0, rotation);
  return {
    x: x + dx - rotated.x,
    y: y + dy - rotated.y,
  };
}
